import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle } from 'lucide-react'
import { ProcessorDetails } from '@/lib/api/processors'
import { useStripeAccountDetails } from '@/lib/api/stripe'

interface StripeAccountDetailsCardProps {
    processor: ProcessorDetails
}

const getAccountId = (options: Record<string, unknown> | null) => {
    return options?.accountId as string | undefined
}

const getChargesEnabled = (options: Record<string, unknown> | null) => {
    return options?.chargesEnabled as boolean | undefined
}

export const StripeAccountDetailsCard = ({ processor }: StripeAccountDetailsCardProps) => {
    const { t } = useTranslation('processors')
    const accountId = getAccountId(processor.options)

    const { data: stripeAccount, isLoading, error } = useStripeAccountDetails(accountId || '')

    if (!accountId) {
        return null
    }

    if (isLoading) {
        return (
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>{t('stripeAccountDetails')}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="animate-pulse space-y-4">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </div>
                </CardContent>
            </Card>
        )
    }

    if (error) {
        return (
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>{t('stripeAccountDetails')}</CardTitle>
                </CardHeader>
                <CardContent>
                    <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t('failedToLoadStripeDetails')}
                        </AlertDescription>
                    </Alert>
                </CardContent>
            </Card>
        )
    }

    if (!stripeAccount) {
        return null
    }

    const isAccountActive = stripeAccount.charges_enabled && stripeAccount.payouts_enabled
    const chargesEnabled = getChargesEnabled(processor.options)

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>{t('stripeAccountDetails')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <p className="text-sm text-muted-foreground">{t('accountStatus')}</p>
                        <Badge variant={isAccountActive ? 'default' : 'secondary'}>
                            {isAccountActive ? t('active') : t('restricted')}
                        </Badge>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('businessType')}</p>
                        <p className="font-semibold">{stripeAccount.business_type || t('notSpecified')}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('country')}</p>
                        <p className="font-semibold">{stripeAccount.country || t('notSpecified')}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('defaultCurrency')}</p>
                        <p className="font-semibold">{stripeAccount.default_currency?.toUpperCase() || 'USD'}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('chargesEnabled')}</p>
                        <Badge variant={stripeAccount.charges_enabled ? 'default' : 'secondary'}>
                            {stripeAccount.charges_enabled ? t('enabled') : t('disabled')}
                        </Badge>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('payoutsEnabled')}</p>
                        <Badge variant={stripeAccount.payouts_enabled ? 'default' : 'secondary'}>
                            {stripeAccount.payouts_enabled ? t('enabled') : t('disabled')}
                        </Badge>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('detailsSubmitted')}</p>
                        <Badge variant={stripeAccount.details_submitted ? 'default' : 'secondary'}>
                            {stripeAccount.details_submitted ? t('yes') : t('no')}
                        </Badge>
                    </div>

                    {stripeAccount.capabilities && (
                        <div>
                            <p className="text-sm text-muted-foreground mb-2">{t('capabilities')}</p>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(stripeAccount.capabilities).map(([key, value]) => (
                                    <Badge key={key} variant="outline">
                                        {key}: {String(value)}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    {!isAccountActive && (
                        <div className="col-span-full">
                            <Alert>
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription>
                                    {t('stripeAccountRestricted')}
                                </AlertDescription>
                            </Alert>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}

